<template>
  <div class="container">
    <!-- Hero -->
    <Hero v-if="isFaqPage && headerText" :breadcrumbTitle="headerText" breadcrumbLink="/faqs" :title="headerText"
      text="" color="green" />

    <div class="lg:py-6 topic-container">
      <div class="flex flex-col md:flex-row">
        <!-- FAQ CONTENT -->
        <div class="hidden md:block w-full md:w-[75%]">
          <div v-for="(faqs, categoryName) in groupedFaqs" :key="categoryName" class="mb-8">
            <h1 class="text-3xl font-semibold my-6" v-if="isFaqPage && headerText">
              {{ categoryName }}
            </h1>

            <ul class="list-none m-0 faq-qa">
              <li v-for="faq in faqs" :key="faq.FAQ_ID" class="py-5 border-gray-200 border-b">
                <h2 v-if="faq.Question" class="text-green-600 text-xl py-2">
                  {{ faq.Question }}
                </h2>

                <div v-if="faq.Answer" class="text-base py-2">
                  {{ faq.Answer }}
                </div>

                <div class="read-more">
                  <NuxtLink :to=faq.LearnMoreURL v-if="faq.LearnMoreURL">
                    Read More...
                  </NuxtLink>
                </div>
                
              </li>
            </ul>
          </div>
        </div>

        <!-- FILTER SIDEBAR -->
        <div class="md:max-w-[25%] md:w-[25%] px-4 sm:px-6 lg:px-8 bg-white md:ml-6 rounded">
          <!-- Search -->
          <div class="bg-white pt-5 pb-8">
            <input v-model="searchText" type="search" placeholder="Search FAQs..."
              class="border border-gray-300 px-4 py-2 w-full" />
          </div>

          <!-- Categories -->
          <h2 class="py-2">Categories</h2>
          <hr />

          <div class="py-5 pl-2 space-y-3">
            <label v-for="category in categories" :key="category.id" class="checkbox cursor-pointer">
              <span class="checkbox__input">
                <input type="checkbox" :checked="selectedCategoryIds.includes(category.id)"
                  @change="toggleCategory(category.id)" />
                <span class="checkbox__control">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <path fill="none" stroke="currentColor" stroke-width="3" d="M1.73 12.91l6.37 6.37L22.79 4.59" />
                  </svg>
                </span>
              </span>
              <p class="text-sm my-2">{{ category.name }}</p>
            </label>
          </div>

          <!-- Topics -->
          <h2 class="py-2 mt-6">Topics</h2>
          <hr />

          <div class="py-5 pl-2 space-y-3">
            <label v-for="topic in topics" :key="topic" class="checkbox cursor-pointer">
              <span class="checkbox__input">
                <input type="checkbox" :checked="selectedTopics.includes(topic)" @change="toggleTopic(topic)" />
                <span class="checkbox__control">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <path fill="none" stroke="currentColor" stroke-width="3" d="M1.73 12.91l6.37 6.37L22.79 4.59" />
                  </svg>
                </span>
              </span>
              <p class="text-sm my-2">{{ topic }}</p>
            </label>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { useNuxtApp, useHead } from '#imports'
import store from '~/store'

interface FaqItem {
  FAQ_ID: number
  Question?: string
  Answer?: string
  CatogaryName?: string
  Topic?: string
  LearnMoreURL?: string
}

export default {
  props: {
    headerText: String,
    isFaqPage: Boolean,
    /** Pre-fetched UCMS FAQ data array. When provided, skips the client-side getAdminFAQs fetch. */
    prefetchedFaqs: {
      type: Array as () => FaqItem[],
      default: null,
    },
    /** When true, the component will not inject its own FAQPage schema (parent page handles it). */
    disableSchema: {
      type: Boolean,
      default: false,
    },
  },

  setup() {
    const { $companies } = useNuxtApp()
    return { companies: $companies }
  },

  data() {
    return {
      filteredServices: [] as FaqItem[],
      categories: [] as { id: number; name: string }[],
      topics: [] as string[],
      selectedCategoryIds: [] as number[],
      selectedTopics: [] as string[],
      searchText: '',
      faqSchemaInjected: false
    }
  },

  computed: {
    activeCategoryNames(): string[] {
      return this.categories
        .filter(c => this.selectedCategoryIds.includes(c.id))
        .map(c => c.name)
    },

    filteredFaqs(): FaqItem[] {
      let result = this.filteredServices

      if (this.activeCategoryNames.length) {
        result = result.filter(f =>
          this.activeCategoryNames.includes(f.CatogaryName || '')
        )
      }

      if (this.selectedTopics.length) {
        result = result.filter(f =>
          this.selectedTopics.includes(f.Topic || '')
        )
      }

      if (this.searchText.trim()) {
        const text = this.searchText.toLowerCase()
        result = result.filter(f =>
          f.Question?.toLowerCase().includes(text) ||
          f.Answer?.toLowerCase().includes(text) 
          // f.CatogaryName?.toLowerCase().includes(text) ||
          // f.Topic?.toLowerCase().includes(text)
        )
      }

      return result
    },

    groupedFaqs(): Record<string, FaqItem[]> {
      return this.filteredFaqs.reduce((acc, faq) => {
        const key = faq.CatogaryName || 'Others'
        acc[key] = acc[key] || []
        acc[key].push(faq)
        return acc
      }, {} as Record<string, FaqItem[]>)
    }
  },

  methods: {
    toggleCategory(id: number) {
      this.selectedCategoryIds.includes(id)
        ? (this.selectedCategoryIds = this.selectedCategoryIds.filter(
          i => i !== id
        ))
        : this.selectedCategoryIds.push(id)
    },

    toggleTopic(topic: string) {
      this.selectedTopics.includes(topic)
        ? (this.selectedTopics = this.selectedTopics.filter(t => t !== topic))
        : this.selectedTopics.push(topic)
    },

    injectFaqSchema() {
      if (this.disableSchema || this.faqSchemaInjected) return

      const mainEntity = this.filteredServices
        .filter(f => f.Question && f.Answer)
        .map(f => ({
          '@type': 'Question',
          name: f.Question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: f.Answer
          }
        }))

      if (!mainEntity.length) return

      useHead({
        script: [
          {
            id: 'faqs-schema',
            type: 'application/ld+json',
            children: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity
            })
          }
        ]
      })

      this.faqSchemaInjected = true
    },

    populateFromData(data: FaqItem[]) {
      this.filteredServices = data || []

      const categorySet = new Set<string>()
      const topicSet = new Set<string>()

      this.filteredServices.forEach(f => {
        if (f.CatogaryName) categorySet.add(f.CatogaryName)
        if (f.Topic) topicSet.add(f.Topic)
      })

      this.categories = [...categorySet].map((name, i) => ({
        id: i + 1,
        name
      }))

      this.topics = [...topicSet]

      this.injectFaqSchema()
    },

    async getAdminFAQs() {
      const sqldbid = store.state.persistedState?.sqldbid || 0

      const data = await this.$companies.getUCMSAdminFAQ({
        categoryId: 0,
        sqldbid,
        uP_ServiceDefinition_IDs: []
      })

      this.populateFromData(data)
    }
  },

  async mounted() {
    // Use pre-fetched data when available (avoids redundant client-side API call)
    if (this.prefetchedFaqs && this.prefetchedFaqs.length > 0) {
      this.populateFromData(this.prefetchedFaqs)
    } else {
      await this.getAdminFAQs()
    }
  }
}
</script>

<style scoped>
p {
  font-size: 16px;
}

svg {
  stroke: #00810E;
}

svg.topic-indicator {
  stroke: #517185;
}

.grey-bg {
  background-color: #EEF1F4;
}


.read-more {
  color: #00810e;
  font-weight: 600;
  margin-top: 10px;
  cursor: pointer;
  text-align: right;
}

.checkbox {
  display: grid;
  grid-template-columns: min-content auto;
  grid-gap: 0.5em;
}

.checkbox__control {
  display: inline-grid;
  width: 1em;
  height: 1em;
  border-radius: 0.25em;
  border: 0.1em solid #00810e;
  background: #fff;
  color: white;
  margin-top: -3px;
}

.checkbox__control svg {
  transition: transform 0.1s ease-in 25ms;
  transform: scale(0);
  transform-origin: bottom left;
}

.checkbox__input {
  display: grid;
  grid-template-areas: "checkbox";
}

.checkbox__input input {
  opacity: 0;
  width: 0;
  height: 0;
}

.checkbox__input input:checked+.checkbox__control svg {
  transform: scale(1);
}

.checkbox__input input:checked+.checkbox__control {
  background: #00810e;
}
</style>
